import BusinessIcon from "@mui/icons-material/Business";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Box, Chip, Link, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { PersonExperienceType } from "../../api";
import { sanitizeHighlight } from "../../utils/sanitize-html";
import { useGlobalState } from "../globalState";
import { LocationDataTooltip } from "../location/LocationDataTooltip";
import {
	sortDisplayGroups,
	sortRolesByDate,
	splitEmployerIntoGroups,
} from "./experience-grouping";
import { ProfileSection } from "./profile-section";
import { ProfileTimeline, ProfileTimelineItem } from "./profile-timeline";

const formatMonthYear = (iso: string | null): string => {
	if (!iso) {
		return "?";
	}
	return format(parseISO(iso), "MMM yyyy");
};

const formatDuration = (years: number | null): string => {
	if (years === null || years === undefined) return "";
	const wholeYears = Math.floor(years);
	const months = Math.round((years - wholeYears) * 12);
	const parts: string[] = [];
	if (wholeYears > 0) {
		parts.push(`${wholeYears} yr${wholeYears !== 1 ? "s" : ""}`);
	}
	if (months > 0) {
		parts.push(`${months} mo${months !== 1 ? "s" : ""}`);
	}
	return parts.join(" ");
};

export const PersonExperience = ({
	experience: initialExperience,
	assignmentId,
	personId,
	hideContractorFlag = false,
}: {
	experience: PersonExperienceType[];
	assignmentId?: number;
	personId: number;
	/** Suppress the "contractor ?" warning — used for parsed-CV candidates,
	 * whose synthetic employers always lack a company_id. */
	hideContractorFlag?: boolean;
}) => {
	const theme = useTheme();
	const { currentUser } = useGlobalState();

	// Check for contractor flags
	let noCompany = false;
	let companySmall = false;
	for (const exp of initialExperience) {
		for (const r of exp.roles) {
			if (r.current) {
				if (!exp.company_id) {
					noCompany = true;
				} else if (!exp.company_size || exp.company_size <= 1) {
					companySmall = true;
				}
			}
		}
	}

	const displayGroups = sortDisplayGroups(
		initialExperience.flatMap((exp) => splitEmployerIntoGroups(exp))
	);

	return (
		<ProfileSection title="Experience" dataComponent="person-experience-card">
			{/* Contractor warning flags */}
			{!hideContractorFlag && (noCompany || companySmall) && (
				<Stack
					direction="row"
					spacing={1}
					sx={{ mb: 2 }}
					data-component="experience-flags"
				>
					<Tooltip
						title={[
							noCompany && "Company without a LinkedIn page",
							companySmall && "Company with 1 employee or less",
						]
							.filter(Boolean)
							.join(" · ")}
						data-component="warning-tooltip"
					>
						<Chip
							variant="outlined"
							color="warning"
							size="small"
							label="contractor ?"
							data-component="contractor-chip"
						/>
					</Tooltip>
				</Stack>
			)}

			{/* Experience entries */}
			<ProfileTimeline dataComponent="experience-list">
				{displayGroups.map((group, groupIdx) => {
					const { employer: exp, roles, groupDuration } = group;
					const hasCurrent = roles.some((r) => r.current);
					const hasMultipleRoles = roles.length > 1;
					const sortedRoles = sortRolesByDate(roles);
					const isLast = groupIdx === displayGroups.length - 1;

					return (
						<ProfileTimelineItem
							key={`${exp.id}-${groupIdx}`}
							current={hasCurrent}
							isLast={isLast}
							dataComponent="experience-company-section"
						>
							{hasMultipleRoles ? (
								// Multiple roles at same company
								<Box>
									<CompanyHeader
										exp={exp}
										isSuperuser={currentUser.is_superuser}
										durationOverride={groupDuration}
									/>
									<Box sx={{ mt: 1 }}>
										<ProfileTimeline dataComponent="roles-timeline">
											{sortedRoles.map((role, idx) => (
												<ProfileTimelineItem
													key={role.id}
													current={role.current}
													isLast={
														idx === sortedRoles.length - 1
													}
													dense
													dataComponent="role-row"
												>
													<RoleContent
														role={role}
														isSuperuser={
															currentUser.is_superuser
														}
														theme={theme}
													/>
												</ProfileTimelineItem>
											))}
										</ProfileTimeline>
									</Box>
								</Box>
							) : (
								// Single role
								<Box>
									{sortedRoles.map((role) => (
										<Box key={role.id} data-component="role-row">
											<RoleContent
												role={role}
												isSuperuser={currentUser.is_superuser}
												theme={theme}
												companyInfo={{
													name: exp.name,
													companyId: exp.company_id,
													linkedInId:
														exp.company_linkedin_named_id,
												}}
											/>
										</Box>
									))}
								</Box>
							)}
						</ProfileTimelineItem>
					);
				})}
			</ProfileTimeline>
		</ProfileSection>
	);
};

// Company header for multiple roles
const CompanyHeader = ({
	exp,
	isSuperuser,
	durationOverride,
}: {
	exp: PersonExperienceType;
	isSuperuser: boolean;
	durationOverride?: number | null;
}) => {
	const duration = formatDuration(durationOverride ?? exp.timedelta_for_employer);

	return (
		<Box data-component="company-header">
			<Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
				<Typography
					component={exp.company_id ? "a" : "span"}
					href={exp.company_id ? `/company/${exp.company_id}/` : undefined}
					sx={{
						fontWeight: 600,
						fontSize: "1rem",
						color: "text.primary",
						textDecoration: "none",
					}}
					data-component="company-name"
				>
					{exp.name}
				</Typography>

				{/* External links */}
				{exp.company_linkedin_named_id ? (
					<Link
						target="_blank"
						href={`https://www.linkedin.com/company/${exp.company_linkedin_named_id}`}
						sx={{ display: "inline-flex", alignItems: "center" }}
						data-component="company-linkedin-link"
					>
						<LinkedInIcon sx={{ fontSize: 18 }} />
					</Link>
				) : (
					<Link
						target="_blank"
						href={`https://www.linkedin.com/search/results/all/?keywords=${exp.name}`}
						sx={{ display: "inline-flex", alignItems: "center" }}
						data-component="company-linkedin-search-link"
					>
						<LinkedInIcon sx={{ fontSize: 18 }} color="secondary" />
					</Link>
				)}
				<Link
					target="_blank"
					href={`https://www.allabolag.se/what/${exp.name}`}
					sx={{ display: "inline-flex", alignItems: "center" }}
					data-component="company-allabolag-link"
				>
					<BusinessIcon sx={{ fontSize: 18 }} />
				</Link>
			</Stack>

			{/* Duration and employee count */}
			<Typography
				variant="caption"
				color="text.secondary"
				data-component="company-duration"
			>
				{duration && (
					<Typography
						component="span"
						variant="caption"
						color="text.secondary"
						sx={{ fontWeight: "bold" }}
					>
						{duration}
					</Typography>
				)}
				{isSuperuser &&
					(exp.company_size || exp.company_ammount_employees_cache) &&
					` · ${exp.company_size} employees (${exp.company_ammount_employees_cache} in DB)`}
				{!isSuperuser && exp.company_size && ` · ${exp.company_size} employees`}
			</Typography>
		</Box>
	);
};

// Role content component
const RoleContent = ({
	role,
	isSuperuser,
	theme,
	companyInfo,
}: {
	role: PersonExperienceType["roles"][0];
	isSuperuser: boolean;
	theme: Theme;
	companyInfo?: {
		name: string;
		companyId: number | null;
		linkedInId: string | null;
	};
}) => {
	const dateRange = `${formatMonthYear(role.start_dt)} - ${role.current ? "Present" : formatMonthYear(role.end_dt)}`;
	const duration = formatDuration(role.timedelta_for_role);

	return (
		<Box sx={{ flex: 1 }} data-component="role-content">
			{/* Role title */}
			{isSuperuser ? (
				<Tooltip
					title={`legacy: [${role.start_date} - ${role.end_date}]`}
					data-component="legacy-field-tooltip"
				>
					<Typography
						sx={{ fontWeight: 600, fontSize: "0.95rem" }}
						dangerouslySetInnerHTML={{
							__html: sanitizeHighlight(role.title),
						}}
						data-component="role-title"
					/>
				</Tooltip>
			) : (
				<Typography
					sx={{ fontWeight: 600, fontSize: "0.95rem" }}
					dangerouslySetInnerHTML={{ __html: sanitizeHighlight(role.title) }}
					data-component="role-title"
				/>
			)}

			{/* Company name (only for single role layout) */}
			{companyInfo && (
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.5}
					data-component="company-info-row"
				>
					<Typography
						component={companyInfo.companyId ? "a" : "span"}
						href={
							companyInfo.companyId
								? `/company/${companyInfo.companyId}/`
								: undefined
						}
						variant="body2"
						sx={{ color: "text.primary", textDecoration: "none" }}
						data-component="company-name"
					>
						{companyInfo.name}
					</Typography>
					{companyInfo.linkedInId ? (
						<Link
							target="_blank"
							href={`https://www.linkedin.com/company/${companyInfo.linkedInId}`}
							sx={{ display: "inline-flex", alignItems: "center" }}
							data-component="company-linkedin-link"
						>
							<LinkedInIcon sx={{ fontSize: 16 }} />
						</Link>
					) : (
						<Link
							target="_blank"
							href={`https://www.linkedin.com/search/results/all/?keywords=${companyInfo.name}`}
							sx={{ display: "inline-flex", alignItems: "center" }}
							data-component="company-linkedin-search-link"
						>
							<LinkedInIcon sx={{ fontSize: 16 }} color="secondary" />
						</Link>
					)}
					<Link
						target="_blank"
						href={`https://www.allabolag.se/what/${companyInfo.name}`}
						sx={{ display: "inline-flex", alignItems: "center" }}
						data-component="company-allabolag-link"
					>
						<BusinessIcon sx={{ fontSize: 16 }} />
					</Link>
				</Stack>
			)}

			{/* Date range and duration */}
			<Typography
				variant="caption"
				color="text.secondary"
				component="div"
				data-component="role-dates"
			>
				{dateRange}
				{duration && (
					<>
						{" · "}
						<Typography
							component="span"
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: "bold" }}
						>
							{duration}
						</Typography>
					</>
				)}
			</Typography>

			{/* Location */}
			{role.location && (
				<LocationDataTooltip
					location_map_id={role.location_map_id}
					ostmd_id={role.ostmd_id}
					locationText={role.location}
					entityId={role.id}
					entityType="Role"
				>
					<Typography
						variant="caption"
						color="text.secondary"
						component="div"
						data-component="role-location"
					>
						{role.location}
					</Typography>
				</LocationDataTooltip>
			)}

			{/* Description */}
			{role.description && (
				<Typography
					variant="body2"
					color="text.primary"
					sx={{ mt: 1 }}
					dangerouslySetInnerHTML={{
						__html: sanitizeHighlight(role.description),
					}}
					data-component="role-description"
				/>
			)}

			{/* Skills */}
			{role.skills && role.skills.length > 0 && (
				<RoleSkills skills={role.skills} />
			)}
		</Box>
	);
};

const SKILLS_COLLAPSED_COUNT = 3;

// Skills chips with expand functionality
const RoleSkills = ({ skills }: { skills: string[] }) => {
	const [expanded, setExpanded] = useState(false);
	const hasMore = skills.length > SKILLS_COLLAPSED_COUNT;
	const visibleSkills = expanded ? skills : skills.slice(0, SKILLS_COLLAPSED_COUNT);
	const hiddenCount = skills.length - SKILLS_COLLAPSED_COUNT;

	return (
		<Stack
			direction="row"
			spacing={0.5}
			sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}
			data-component="role-skills"
		>
			{visibleSkills.map((skill, idx) => (
				<Chip
					key={idx}
					label={skill}
					size="small"
					variant="outlined"
					data-component="skill-chip"
				/>
			))}
			{hasMore && !expanded && (
				<Chip
					label={`+${hiddenCount} more`}
					size="small"
					variant="outlined"
					onClick={() => setExpanded(true)}
					sx={{ cursor: "pointer" }}
					data-component="expand-skills-chip"
				/>
			)}
			{hasMore && expanded && (
				<Chip
					label="Show less"
					size="small"
					variant="outlined"
					onClick={() => setExpanded(false)}
					sx={{ cursor: "pointer" }}
					data-component="collapse-skills-chip"
				/>
			)}
		</Stack>
	);
};
